/**
 * 웹 Shell 메뉴 아이콘 — lucide-react + 앱(Flutter) quick action id(appIconKey) 대응.
 * 앱에서 아이콘이 확정되면 appIconKey 기준으로 FE 매핑만 갱신하면 됩니다.
 */
import {
  LayoutDashboard,
  IdCard,
  Globe,
  Cloud,
  Building2,
  Layers,
  Headphones,
  ClipboardList,
  CalendarClock,
  Users,
  Calculator,
  FileText,
  Image,
  MessageCircle,
  Banknote,
  MessagesSquare,
  Stethoscope,
  PiggyBank,
  Flame,
  Search,
  Megaphone,
  Newspaper,
  Monitor,
  Landmark,
  CalendarDays,
  GraduationCap,
  Wrench,
  BarChart3,
  UserCog,
  ImageIcon,
  CheckCircle,
  DoorOpen,
  CircleDot,
  Settings,
  Link2,
  FolderOpen,
  Shield,
  Database,
  ScrollText,
  FileStack,
  Bell,
  Tag,
  BookOpen,
  FlaskConical,
  HelpCircle,
  History,
  Printer,
  Puzzle,
  LayoutTemplate,
  AlertCircle,
} from 'lucide-react'

/** @type {Record<string, { icon: import('lucide-react').LucideIcon, appIconKey?: string }>} */
export const MENU_ICON_REGISTRY = {
  dashboard: { icon: LayoutDashboard, appIconKey: 'home.dashboard' },
  'public-card': { icon: IdCard, appIconKey: 'sales.publicCard' },
  'public-landing': { icon: Globe, appIconKey: 'sales.publicLanding' },
  'org-homepage': { icon: Building2, appIconKey: 'sales.orgHomepage' },
  'category-landing-pages': { icon: Layers, appIconKey: 'sales.categoryLanding' },
  consultations: { icon: Headphones, appIconKey: 'sales.consultations' },
  'customer-work-queue': { icon: LayoutDashboard, appIconKey: 'support.customerWorkQueue' },
  'maturity-ledger': { icon: CalendarClock, appIconKey: 'support.maturityLedger' },
  customers: { icon: Users, appIconKey: 'support.customers' },
  'customer-support-tools': { icon: Calculator, appIconKey: 'support.customerTools' },
  'survey-management': { icon: FileText, appIconKey: 'support.surveys' },
  'sales-images': { icon: Image, appIconKey: 'sales.images' },
  'kakao-bulk-sender': { icon: MessageCircle, appIconKey: 'support.kakaoBulk' },
  claims: { icon: Banknote, appIconKey: 'support.claims' },
  'bulk-sms': { icon: MessagesSquare, appIconKey: 'support.bulkSms' },
  'health-analysis': { icon: Stethoscope, appIconKey: 'health.analysis' },
  'hidden-insurance-money': { icon: PiggyBank, appIconKey: 'hidden.insurance' },
  'disease-medical-fee-stats': { icon: BarChart3 },
  'fire-building-registry': { icon: Flame, appIconKey: 'sales.fire' },
  'uw3nn-exceptions': { icon: Search, appIconKey: 'uw3nn.exceptions' },
  requests: { icon: ClipboardList, appIconKey: 'requests.list' },
  'design-reference-hub': { icon: BookOpen, appIconKey: 'design.referenceHub' },
  'design-disease-rider': { icon: Stethoscope, appIconKey: 'design.diseaseRider' },
  'design-surgery-search': { icon: Search, appIconKey: 'design.surgerySearch' },
  'design-major-disease-n': { icon: FlaskConical, appIconKey: 'design.majorDiseaseN' },
  notices: { icon: Megaphone, appIconKey: 'work.notices' },
  'personal-task-board': { icon: ClipboardList, appIconKey: 'work.personalTaskBoard' },
  'insurance-news': { icon: Newspaper, appIconKey: 'work.insuranceNews' },
  'insurer-newsletters': { icon: Newspaper, appIconKey: 'work.insurerNewsletters' },
  'insurer-systems': { icon: Monitor, appIconKey: 'work.insurerSystems' },
  'insurer-business-hub': { icon: Landmark, appIconKey: 'work.insurerBusinessHub' },
  'work-calendar': { icon: CalendarDays, appIconKey: 'work.calendar' },
  'education-center': { icon: GraduationCap, appIconKey: 'work.education' },
  'work-support-tools': { icon: Wrench, appIconKey: 'work.supportTools' },
  orgs: { icon: Building2, appIconKey: 'admin.orgs' },
  'org-banners': { icon: ImageIcon, appIconKey: 'admin.orgBanners' },
  users: { icon: UserCog, appIconKey: 'people.usersOrDirectory' },
  'org-dashboard': { icon: LayoutDashboard },
  reports: { icon: BarChart3, appIconKey: 'admin.reports' },
  signups: { icon: CheckCircle, appIconKey: 'admin.signups' },
  'withdrawal-approvals': { icon: DoorOpen, appIconKey: 'admin.withdrawal' },
  availability: { icon: CircleDot, appIconKey: 'admin.availability' },
  'subscriptions-dashboard': { icon: LayoutDashboard, appIconKey: 'ops.subscriptionsDashboard' },
  'subscriptions-config': { icon: Settings, appIconKey: 'ops.subscriptionsConfig' },
  'subscriptions-orgs': { icon: Building2, appIconKey: 'ops.subscriptionsOrgs' },
  'subscriptions-hub': { icon: Link2, appIconKey: 'ops.subscriptionsHub' },
  'exception-rules': { icon: FileText, appIconKey: 'data.exceptionRules' },
  'insurers-master': { icon: Landmark, appIconKey: 'data.insurersMaster' },
  permissions: { icon: Shield, appIconKey: 'data.permissions' },
  'raw-data-management': { icon: Database, appIconKey: 'data.raw' },
  'public-data-management': { icon: Cloud },
  'terms-management': { icon: ScrollText },
  'design-support-reference-data': { icon: BookOpen, appIconKey: 'data.designSupport' },
  files: { icon: FolderOpen, appIconKey: 'data.files' },
  'ops-usage-dashboard': { icon: LayoutDashboard, appIconKey: 'ops.usageDashboard' },
  'ops-operational-costs': { icon: Banknote },
  'ops-operational-stats': { icon: BarChart3 },
  'system-notices': { icon: Megaphone, appIconKey: 'ops.systemNotices' },
  'ops-banners': { icon: ImageIcon, appIconKey: 'ops.banners' },
  'support-inquiries': { icon: HelpCircle, appIconKey: 'ops.supportInquiries' },
  'ops-history-management': { icon: History, appIconKey: 'ops.history' },
  'ops-withdrawal-approvals': { icon: DoorOpen, appIconKey: 'ops.withdrawal' },
  'ops-claim-templates': { icon: FileStack, appIconKey: 'ops.claimTemplates' },
  'ops-claim-fax': { icon: Printer, appIconKey: 'ops.claimFax' },
  'ops-claim-stats': { icon: BarChart3, appIconKey: 'ops.claimStats' },
  'after-contract-certificates': { icon: FileText, appIconKey: 'ops.afterContractCerts' },
  'policy-delivery-book-templates': { icon: BookOpen, appIconKey: 'ops.policyDeliveryBooks' },
  'report-output-templates': { icon: LayoutTemplate },
  'homepage-templates': { icon: LayoutTemplate, appIconKey: 'ops.orgHomepageTemplates' },
  'org-homepage-templates': { icon: LayoutTemplate, appIconKey: 'ops.orgHomepageTemplates' },
  'org-homepage-assets': { icon: ImageIcon, appIconKey: 'ops.orgHomepageAssets' },
  'user-homepage-templates': { icon: Globe, appIconKey: 'ops.userHomepageTemplates' },
  'landing-page-templates': { icon: Puzzle, appIconKey: 'ops.landingTemplates' },
  'survey-templates': { icon: ClipboardList, appIconKey: 'ops.surveyTemplates' },
  'message-templates': { icon: MessagesSquare, appIconKey: 'ops.messageTemplates' },
  'ops-alimtalk-templates': { icon: MessageCircle, appIconKey: 'ops.alimtalkTemplates' },
  'ops-alert-management': { icon: Bell, appIconKey: 'ops.alertManagement' },
  'ops-external-program-versions': { icon: Tag, appIconKey: 'ops.externalVersions' },
  'ops-kakao-talk-management': { icon: MessageCircle, appIconKey: 'ops.kakaoTalk' },
  'ops-legal-policy-management': { icon: ScrollText, appIconKey: 'ops.legalPolicy' },
  'ops-support-guide-manuals': { icon: BookOpen, appIconKey: 'ops.supportGuideManuals' },
  'ops-operation-tests': { icon: FlaskConical, appIconKey: 'ops.apiTest' },
  'ops-api-test': { icon: FlaskConical, appIconKey: 'ops.apiTest' },
}

export function getMenuIconDef(menuItemId) {
  if (!menuItemId) return null
  return MENU_ICON_REGISTRY[menuItemId] || null
}

export function getMenuLucideIcon(menuItemId) {
  return getMenuIconDef(menuItemId)?.icon || AlertCircle
}
